import styled, { css } from 'styled-components';

export type VerificationStatus = 'verified' | 'in_review' | 'unverified';

export const theme = {
  text: '#0f2a33',
  muted: '#62737a',
  border: '#e5ecea',
  surface: '#ffffff',
  background: '#f8fbfa',
  green: '#12805c',
  greenSoft: '#e3f3ec',
  blue: '#3b82f6',
  purple: '#a78bfa',
  purpleDark: '#6d4fd8',
  purpleSoft: '#f6f3ff',
  yellow: '#b7791f',
  yellowSoft: '#fdf6e7',
  red: '#c53d3d',
  redSoft: '#fcecec',
};

const breakpoints = {
  wide: '1280px',
  tablet: '1024px',
  phone: '640px',
};

export const Layout = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 248px minmax(0, 1fr);
  background: ${theme.background};
  font-family: 'Manrope', sans-serif;
  color: ${theme.text};

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const SidebarSlot = styled.div<{ open: boolean }>`
  @media (max-width: ${breakpoints.tablet}) {
    display: ${({ open }) => (open ? 'block' : 'none')};
  }
`;

export const Topbar = styled.header`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 32px;
  border-bottom: 1px solid ${theme.border};
  background: ${theme.surface};

  .menu-toggle {
    display: none;
    margin-right: auto;
    border: none;
    background: transparent;
    color: ${theme.text};
    cursor: pointer;

    svg {
      width: 28px;
      height: 28px;
    }
  }

  .user {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: ${theme.text};

    img {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      object-fit: cover;
    }

    strong {
      display: block;
      font-size: 14px;
      text-transform: capitalize;
    }

    span {
      font-size: 12px;
      color: ${theme.muted};
    }
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 12px 16px;

    .menu-toggle {
      display: block;
    }
  }

  @media (max-width: ${breakpoints.phone}) {
    .user div {
      display: none;
    }
  }
`;

export const Content = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  padding: 24px 32px 32px;

  @media (max-width: ${breakpoints.wide}) {
    grid-template-columns: minmax(0, 1fr);
  }

  @media (max-width: ${breakpoints.tablet}) {
    padding: 16px;
    gap: 16px;
  }
`;

export const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;

  /* On small screens the profile status comes before the highlights */
  @media (max-width: ${breakpoints.tablet}) {
    gap: 16px;

    > *,
    > * > * {
      order: 4;
    }

    .order-title {
      order: 1;
    }

    .order-profile {
      order: 2;
    }

    .order-highlights {
      order: 3;
    }
  }
`;

export const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const PageTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;

  h1 {
    font-size: 32px;
    font-weight: 800;
    text-transform: capitalize;
  }

  p {
    font-size: 15px;
    color: ${theme.muted};
  }

  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  @media (max-width: ${breakpoints.phone}) {
    h1 {
      font-size: 26px;
    }
  }
`;

export const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  background: ${theme.surface};
  font-size: 13px;
  font-weight: 600;

  svg {
    width: 16px;
    height: 16px;
    color: ${theme.muted};
  }

  select {
    border: none;
    background: transparent;
    font: inherit;
    color: inherit;
    cursor: pointer;
    outline: none;
  }
`;

export const Grid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, minmax(0, 1fr));
  gap: 20px;

  /* Cards join the page flow so they can be reordered on small screens */
  @media (max-width: ${breakpoints.tablet}) {
    display: contents;
  }
`;

export const Card = styled.section<{ tinted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 20px;
  border: 1px solid ${theme.border};
  border-radius: 16px;
  background: ${({ tinted }) => (tinted ? theme.greenSoft : theme.surface)};

  @media (max-width: ${breakpoints.phone}) {
    padding: 16px;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  h2 {
    font-size: 16px;
    font-weight: 800;
  }

  span {
    font-size: 13px;
    color: ${theme.muted};
  }
`;

export const Score = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 24px;

  .ring {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${theme.muted};
  }

  @media (max-width: ${breakpoints.phone}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Bars = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;

  li {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 6px 12px;
    font-size: 13px;

    > div {
      grid-column: 1 / -1;
    }
  }

  .value {
    color: ${theme.muted};
  }
`;

export const Bar = styled.div<{ value: number; color?: string }>`
  height: 8px;
  border-radius: 8px;
  background: ${theme.border};
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ value }) => Math.max(0, Math.min(100, value))}%;
    border-radius: 8px;
    background: ${({ color }) => color || theme.green};
    transition: width 0.4s ease;
  }
`;

export const Insight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  font-size: 13px;

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: ${theme.green};
  }
`;

export const Compare = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border: 1px solid ${theme.border};
  border-radius: 12px;

  .compare-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 700;

    svg {
      width: 20px;
      height: 20px;
      color: ${theme.green};
    }
  }

  .compare-values {
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr);
    align-items: end;
    gap: 24px;

    small {
      display: block;
      font-size: 12px;
      color: ${theme.muted};
    }

    strong {
      font-size: 22px;
      font-weight: 800;
    }

    .regional {
      font-size: 18px;
      font-weight: 600;
    }
  }

  @media (max-width: ${breakpoints.phone}) {
    .compare-values {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

export const Range = styled.div<{ position: number }>`
  grid-column: span 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 140px;

  .track {
    position: relative;
    height: 6px;
    border-radius: 6px;
    background: ${theme.border};
  }

  .marker {
    position: absolute;
    top: 50%;
    left: ${({ position }) => position}%;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${theme.green};
    border: 2px solid ${theme.surface};
    box-shadow: 0 0 0 1px ${theme.green};
    transform: translate(-50%, -50%);
  }

  .limits {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: ${theme.muted};
  }

  @media (max-width: ${breakpoints.phone}) {
    grid-column: 1 / -1;
  }
`;

export const Legend = styled.ul`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  list-style: none;
  font-size: 13px;

  li {
    display: grid;
    grid-template-columns: 10px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
  }

  i {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  span {
    color: ${theme.muted};
  }

  strong {
    font-weight: 700;
  }
`;

export const DonutRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
`;

export const Stats = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${({ columns }) => columns || 2},
    minmax(0, 1fr)
  );
  gap: 12px;

  @media (max-width: ${breakpoints.phone}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid ${theme.border};
  border-radius: 12px;

  > svg {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    color: ${theme.green};
  }

  small {
    display: block;
    font-size: 12px;
    color: ${theme.muted};
  }

  strong {
    font-size: 20px;
    font-weight: 800;
  }
`;

export const Hint = styled.p`
  font-size: 13px;
  color: ${theme.muted};
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: filter 0.2s;

  &:hover {
    filter: brightness(0.95);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const WhatsappButton = styled.a`
  ${buttonBase}
  border: none;
  background: #25d366;
  color: #fff;
`;

export const OutlineButton = styled.button`
  ${buttonBase}
  border: 1px solid ${theme.purpleDark};
  background: ${theme.surface};
  color: ${theme.purpleDark};
`;

export const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: ${theme.green};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Opportunities = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border: 1px solid #ebe5fb;
  border-radius: 16px;
  background: ${theme.purpleSoft};

  h2 {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 800;
    color: ${theme.purpleDark};

    svg {
      width: 20px;
      height: 20px;
    }
  }

  > p {
    font-size: 13px;
    color: ${theme.muted};
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 10px;
    list-style: none;
  }

  ${OutlineButton} {
    width: 100%;
  }
`;

const toneColors = {
  danger: theme.red,
  warning: theme.yellow,
  info: theme.purpleDark,
};

export const Opportunity = styled.button<{ tone: keyof typeof toneColors }>`
  width: 100%;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) 16px;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: ${theme.surface};
  text-align: left;
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 16px rgba(109, 79, 216, 0.12);
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${theme.purpleSoft};
    color: ${({ tone }) => toneColors[tone]};

    svg {
      width: 18px;
      height: 18px;
    }
  }

  strong {
    display: block;
    font-size: 13px;
    color: ${theme.text};
  }

  span {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: ${theme.muted};
  }

  > svg {
    width: 16px;
    height: 16px;
    color: ${theme.muted};
  }
`;

export const Motto = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 16px;
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  font-size: 14px;
  color: ${theme.muted};

  svg {
    width: 20px;
    height: 20px;
    color: ${theme.green};
  }
`;

const statusColors: Record<VerificationStatus, string> = {
  verified: theme.green,
  in_review: theme.yellow,
  unverified: theme.muted,
};

export const StatusBadge = styled.span<{ status: VerificationStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ status }) => statusColors[status]};

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Highlights = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  gap: 20px;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const PremiumHighlight = styled.section<{ active: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  padding: 24px;
  border-radius: 20px;
  overflow: hidden;
  color: #fff;
  background: ${({ active }) =>
    active
      ? `linear-gradient(135deg, ${theme.green} 0%, #0b5c42 100%)`
      : `linear-gradient(135deg, ${theme.text} 0%, #0b5c42 60%, ${theme.green} 100%)`};
  box-shadow: 0 12px 32px -12px rgba(15, 42, 51, 0.45);

  &::after {
    content: '';
    position: absolute;
    right: -60px;
    top: -60px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    background: #f5c451;
    color: ${theme.text};
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;

    svg {
      width: 14px;
      height: 14px;
      fill: currentColor;
    }
  }

  h2 {
    font-size: 24px;
    font-weight: 800;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 8px;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
    }

    svg {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      color: #7ee0b1;
    }
  }

  .context {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
  }

  .cta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    margin-top: 4px;

    span {
      font-size: 14px;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.85);
    }
  }

  @media (max-width: ${breakpoints.phone}) {
    padding: 20px;

    .cta {
      width: 100%;

      button {
        width: 100%;
      }
    }
  }
`;

export const PremiumButton = styled.button`
  ${buttonBase}
  min-height: 48px;
  padding: 0 24px;
  border: none;
  background: #f5c451;
  color: ${theme.text};
  font-size: 15px;
  font-weight: 800;
`;

export const ShareHighlight = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border: 1px solid #c9ecd6;
  border-radius: 20px;
  background: linear-gradient(180deg, #eafaf0 0%, ${theme.surface} 100%);

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #25d366;
    color: #fff;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  h2 {
    font-size: 20px;
    font-weight: 800;
  }

  p {
    font-size: 14px;
    color: ${theme.muted};
  }

  ${WhatsappButton} {
    min-height: 48px;
    font-size: 15px;
  }

  .links {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 20px;
  }

  @media (max-width: ${breakpoints.phone}) {
    padding: 20px;
  }
`;
